/**
 * Phase 4b PR 4 — useFeatureFlag hook.
 *
 * Reactive wrapper around `lib/featureFlag.ts`. Reads the flag once on
 * mount; the flag does not change during a session (URL params on first
 * load determine the value, then localStorage caches it).
 *
 * If we ever need to support live flag toggling (e.g., a debug panel
 * that flips flags without reload), add a `useSyncExternalStore` here.
 * For Phase 4 the once-on-mount semantic is correct.
 */

import { useState } from 'react';
import {
  isCanonicalPlanTableEnabled,
  isCoachInsightsEnabled,
  isPlanNudgesEnabled,
  isReactChatV2Enabled,
} from '../lib/featureFlag';

export function useReactChatV2Enabled(): boolean {
  // useState initializer runs once — same evaluation every render.
  const [enabled] = useState<boolean>(() => isReactChatV2Enabled());
  return enabled;
}

/** CI-1 — coach-insight rules engine on the /v2/today coach surfaces. */
export function useCoachInsightsEnabled(): boolean {
  const [enabled] = useState<boolean>(() => isCoachInsightsEnabled());
  return enabled;
}

/** NU-2 — plan-lifecycle nudges on the proactive-notification pipeline. */
export function usePlanNudgesEnabled(): boolean {
  const [enabled] = useState<boolean>(() => isPlanNudgesEnabled());
  return enabled;
}

/** NP-0 — canonical DB-truth plan table on persisted chat turns. */
export function useCanonicalPlanTableEnabled(): boolean {
  const [enabled] = useState<boolean>(() => isCanonicalPlanTableEnabled());
  return enabled;
}
